import { Component, Inject, OnInit } from '@angular/core';
import { LookupV2Service } from '@services/lookup-v2/lookup-v2.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    EditSegmentNameParams,
    SystemSegment,
    SystemSegmentPopupProps,
    HiatusWeekSelection,
    RateCardMetricRowV2,
    EMPTY_SUBTITLE_LABEL,
} from '@models/rate-card/rate-card';
import { RateCardService } from '@services/rate-card/rate-card.service';
import { Segmentation, UiConfig } from '@models/config';
import { AuthService } from '@services/auth/auth.service';
import { forkJoin, Observable, of } from 'rxjs';
import { AutocompleteFilter, StringFilter, MultiSelectFilter, TimeFilter, SelectFilter, BooleanFilter } from '@models/filter-types';
import { NotificationService } from '@services/notification/notification.service';
import { MessageDialogComponent } from '@shared/components/message-dialog/message-dialog.component';
import { LookupItem, StringLookupItem } from '@models/lookup';
import moment from 'moment';

@Component({
    selector: 'app-edit-system-segment-popup',
    templateUrl: './edit-system-segment-popup.component.html',
    styleUrls: ['./edit-system-segment-popup.component.scss'],
    standalone: false,
})
export class EditSystemSegmentPopupComponent implements OnInit {
    config!: UiConfig;
    segmentTimeFormat: string;
    trimSegmentTimeMeridiem = false;
    sortedFilteredSegments: Segmentation[];
    sortedStringSegments: Segmentation[];
    segments: {
        segment: SystemSegment;
        newSegmentFilters: AutocompleteFilter[] | TimeFilter[];
        originalSegmentFilter: StringFilter;
        segmentHeader: string;
    }[] = [];
    airedWeeksFilter!: MultiSelectFilter<string>;
    daypartFilter!: SelectFilter<LookupItem>;
    originalDaypartFilter!: StringFilter;
    originalDaypartId!: number;
    currentDaypartId!: number;
    subtitleFilter!: StringFilter;
    originalSubtitleFilter!: StringFilter;
    editOnDynamicRateCard: BooleanFilter;

    constructor(
        private lookupSvc: LookupV2Service,
        private authSvc: AuthService,
        private snackBarSvc: NotificationService,
        private rateCardSvc: RateCardService,
        public dialogRef: MatDialogRef<EditSystemSegmentPopupComponent>,
        @Inject(MAT_DIALOG_DATA) public data: SystemSegmentPopupProps,
    ) {
        this.config = this.authSvc.getConfig();
        this.editOnDynamicRateCard = new BooleanFilter('Edit on Dynamic Rate Card', true).Slider(true).LabelPosition('after');
        this.segmentTimeFormat = this.config.customerConfig.segment_time_format
            ? (this.config.customerConfig.segment_time_format as string)
            : 'hh:mm A';
        if (this.segmentTimeFormat.includes('-1')) {
            this.segmentTimeFormat = this.segmentTimeFormat.replace('-1', '');
            this.trimSegmentTimeMeridiem = true;
        }
        const segmentation = data.isPolitical
            ? this.config.channelSegmentation[this.data.channelId].political_lur
            : this.config.channelSegmentation[this.data.channelId].rate_card;
        this.sortedFilteredSegments = segmentation
            .sort((first, second) => first.segment_order - second.segment_order)
            .filter((segment) => !segment.is_dow);
        this.sortedStringSegments = segmentation
            .sort((first, second) => first.segment_order - second.segment_order)
            .filter((segment) => !segment.is_dow && !segment.is_start_time && !segment.is_end_time);
    }

    ngOnInit() {
        const subs: Observable<HiatusWeekSelection[] | SystemSegment | LookupItem[]>[] = [];

        if (this.hasSubtitle()) {
            this.originalSubtitleFilter = new StringFilter('Original')
                .Default(this.data.subtitle?.editInfo?.originalName ?? EMPTY_SUBTITLE_LABEL)
                .Readonly(true)
                .SkipValidate(true);
            this.subtitleFilter = new StringFilter('New').Default(this.data.subtitle?.value ?? '').SkipValidate(true);
        }

        this.sortedFilteredSegments.forEach((segment) => {
            subs.push(
                this.lookupSvc.getSystemSegments(
                    this.data.channelId,
                    this.data.segmentation[segment.segment_type],
                    segment.segment_type,
                    this.data.baseSegmentationId,
                    this.data.isPolitical,
                ),
            );
        });
        subs.push(
            this.lookupSvc.getSystemSegmentAiredWeeks(
                this.data.channelId,
                this.data.baseSegmentationId,
                this.data.reloadParams.startDate,
                this.data.reloadParams.endDate,
            ),
        );
        subs.push(this.lookupSvc.getDayparts([this.data.channelId], [this.data.spotTypeId], {}));

        forkJoin(subs).subscribe((data) => {
            (data.slice(0, -2) as SystemSegment[]).forEach((segment) => {
                const fullSegment = this.sortedFilteredSegments.find((seg) => seg.segment_type === segment.segmentType);
                const segmentBuilt = {
                    segment,
                    segmentHeader: this.sortedFilteredSegments.find((segmentation) => segmentation.segment_type === segment.segmentType)
                        .segment_col_header,
                    originalSegmentFilter: new StringFilter('Original').Default(segment.originalName).Readonly(true).SkipValidate(true),
                    newSegmentFilters: this.createSegmentFilter(segment, fullSegment),
                };
                this.segments.push(segmentBuilt);
            });
            this.segments.forEach((segment) => {
                segment.newSegmentFilters.forEach((segmentFilter) => {
                    if (segmentFilter instanceof AutocompleteFilter) {
                        segmentFilter.onReset = () => {
                            segmentFilter.Value = null;
                        };
                        segmentFilter.Load().subscribe((options) => {
                            if (segmentFilter instanceof AutocompleteFilter) {
                                segmentFilter.finishLoad(options);
                            }
                        });
                    }
                });
            });
            const hiatusReturn = data.slice(-2)[0] as HiatusWeekSelection[];
            const weeks = hiatusReturn.map((x) => x.week);
            const airedWeeks = hiatusReturn.filter((x) => x.isAired).map((x) => x.week);
            this.airedWeeksFilter = new MultiSelectFilter<string>('Aired Weeks')
                .Options(weeks)
                .Default(airedWeeks || [])
                .Clear(() => (this.airedWeeksFilter.Value = []))
                .TriggerText((o, s) => (o.length === s.length ? 'All ' + 'Weeks' : `${s.length} ` + ' Weeks' + ' Selected'));

            const dayparts = data.slice(-1)[0] as LookupItem[];

            let currentDaypartId: number;

            if (this.data.daypart?.editInfo) {
                this.originalDaypartId = this.data.daypart.editInfo.originalDaypartId;
                currentDaypartId = this.data.daypart.editInfo.overrideDaypartId;
            } else {
                this.originalDaypartId = this.data.daypartId;
                currentDaypartId = this.data.daypartId;
            }

            this.currentDaypartId = currentDaypartId;
            const currentDaypart = dayparts.find((d) => d.id === currentDaypartId);
            const originalDaypart = dayparts.find((d) => d.id === this.originalDaypartId);

            this.originalDaypartFilter = new StringFilter('Original')
                .Default(originalDaypart?.name || '')
                .Readonly(true)
                .SkipValidate(true);

            this.daypartFilter = new SelectFilter<LookupItem>('Daypart')
                .Options(dayparts)
                .Transform((item: LookupItem) => item.id)
                .Default(currentDaypart);

            const segmentsSyncedToDynamic = (data.slice(0, -2) as SystemSegment[]).every(
                (segment) => segment.editOnDynamicRateCard !== false,
            );
            const daypartSyncedToDynamic = this.data.daypart?.editInfo?.editOnDynamicRateCard !== false;
            this.editOnDynamicRateCard.Value = segmentsSyncedToDynamic && daypartSyncedToDynamic;
        });
    }

    hasSubtitle(): boolean {
        return this.data.periodStartDate != null && this.data.subtitle != null;
    }

    save(): void {
        const airedWeeks = this.lookupSvc.saveSystemSegmentAiredWeeks(
            this.data.channelId,
            this.data.baseSegmentationId,
            this.airedWeeksFilter?.Value,
            this.data.reloadParams.startDate,
            this.data.reloadParams.endDate,
            this.data.reloadParams,
            this.data.rateCardTab,
        );

        const selectedDaypartId = (this.daypartFilter?.Value as LookupItem)?.id || this.originalDaypartId;

        const saveSegments = this.segments.map((segment) => ({
            channelId: this.data.channelId,
            baseSegmentationId: this.data.baseSegmentationId,
            daypartId: this.data.daypartId,
            spotTypeId: this.data.spotTypeId,
            dynamicSegmentation: this.data.segmentation,
            segmentId: segment.segment.segmentId,
            segmentType: segment.segment.segmentType,
            segmentName: this.getFinalSegmentName(segment.originalSegmentFilter, segment.newSegmentFilters, segment.segment),
        }));

        // Always send daypart override - backend will handle reversion detection
        const daypartOverride = {
            channelId: this.data.channelId,
            baseSegmentationId: this.data.baseSegmentationId,
            daypartId: this.data.daypartId,
            spotTypeId: this.data.spotTypeId,
            dynamicSegmentation: this.data.segmentation,
            overrideDaypartId: selectedDaypartId,
        };

        const textDialogRef = this.data.matDialog.open(MessageDialogComponent, {
            disableClose: true,
            data: {
                message: 'Please wait while this change is saved',
            },
        });
        const nextSubtitle = (this.subtitleFilter?.Value ?? '').trim();
        const subtitleChanged = this.hasSubtitle() && nextSubtitle !== (this.data.subtitle?.value ?? '');

        forkJoin({
            airedWeeks,
            segments: this.lookupSvc.saveSystemSegmentEdits(
                // @ts-expect-error TS7053: implicit any
                saveSegments,
                daypartOverride,
                this.editOnDynamicRateCard.Value,
                this.data.isPolitical,
            ) as Observable<EditSegmentNameParams[]>,
            subtitle: subtitleChanged
                ? this.rateCardSvc.saveSegmentSubtitle({
                      channelId: this.data.channelId,
                      baseSegmentationId: this.data.baseSegmentationId,
                      periodStartDate: this.data.periodStartDate,
                      subtitle: nextSubtitle,
                  })
                : of(undefined),
        }).subscribe({
            next: ({ airedWeeks: editedHiatusRow, segments: savedSegments, subtitle: savedSubtitle }) => {
                this.data.getRateCardSegments();
                this.data.afterSave(savedSegments, editedHiatusRow as RateCardMetricRowV2[], savedSubtitle);
                textDialogRef.close();
                this.dialogRef.close();
            },
            error: () => {
                this.snackBarSvc.openSnackBar(`Failed to save program details`, 'error');
                textDialogRef.close();
            },
        });
    }

    createSegmentFilter(segment: SystemSegment, fullSegment: Segmentation): AutocompleteFilter[] | TimeFilter[] {
        if (
            (fullSegment.is_start_time && !fullSegment.is_end_time) ||
            (!fullSegment.is_start_time && fullSegment.is_end_time) ||
            (fullSegment.is_start_time && fullSegment.is_end_time && !segment.originalName.includes('-'))
        ) {
            return [
                new TimeFilter('Time').Default(
                    moment(segment.currentName ? segment.currentName : segment.originalName, this.segmentTimeFormat),
                ),
            ];
        }
        if (fullSegment.is_start_time && fullSegment.is_end_time && segment.originalName.includes('-')) {
            const segmentToParse = segment.currentName ? segment.currentName : segment.originalName;
            const startTimeToParse = segmentToParse.split('-')[0];
            const endTimeToParse = segmentToParse.split('-')[1];
            const startTiemFilter = new TimeFilter('Start Time').Default(moment(startTimeToParse, this.segmentTimeFormat)).Required(true);
            const endTimeFilter = new TimeFilter('End Time').Default(moment(endTimeToParse, this.segmentTimeFormat)).Required(true);
            return [startTiemFilter, endTimeFilter];
        } else {
            return [
                new AutocompleteFilter<StringLookupItem | LookupItem>('New')
                    .Defer(() =>
                        this.lookupSvc.getRateCardSegments(
                            [this.data.channelId],
                            [this.data.daypartId],
                            this.sortedFilteredSegments.find((seg) => seg.segment_type === segment.segmentType),
                        ),
                    )
                    .Transform((item) => (item as StringLookupItem | LookupItem).name)
                    .Default(segment.currentName),
            ];
        }
    }

    getFinalSegmentName(
        originalSegmentFilter: StringFilter,
        newSegmentFilters: AutocompleteFilter[] | TimeFilter[],
        segment: SystemSegment,
    ): string {
        if (!newSegmentFilters[0].Value) {
            return originalSegmentFilter.Value;
        } else if (
            newSegmentFilters.length > 1 &&
            newSegmentFilters[0] instanceof TimeFilter &&
            newSegmentFilters[1] instanceof TimeFilter
        ) {
            if (
                segment.currentName == null &&
                moment(originalSegmentFilter.Value.split(' - ')[0], this.segmentTimeFormat).isSame(moment(newSegmentFilters[0].Value)) &&
                moment(originalSegmentFilter.Value.split(' - ')[1], this.segmentTimeFormat).isSame(moment(newSegmentFilters[1].Value))
            ) {
                return originalSegmentFilter.Value;
            }
            let startSegmentTimeName = newSegmentFilters[0].Value.format(this.segmentTimeFormat);
            let endSegmentTimeName = newSegmentFilters[1].Value.format(this.segmentTimeFormat);
            if (this.trimSegmentTimeMeridiem) {
                startSegmentTimeName = startSegmentTimeName.slice(0, -1);
                endSegmentTimeName = endSegmentTimeName.slice(0, -1);
            }
            return startSegmentTimeName + '-' + endSegmentTimeName;
        } else if (
            segment.currentName == null &&
            newSegmentFilters[0] instanceof TimeFilter &&
            moment(newSegmentFilters[0].Value).isSame(moment(originalSegmentFilter.Value, this.segmentTimeFormat))
        ) {
            return originalSegmentFilter.Value;
        } else if (newSegmentFilters[0] instanceof TimeFilter) {
            const segmentTimeName = newSegmentFilters[0].Value.format(this.segmentTimeFormat);
            if (this.trimSegmentTimeMeridiem) {
                return segmentTimeName.slice(0, -1);
            }
            return segmentTimeName;
        } else if (newSegmentFilters[0] instanceof AutocompleteFilter) {
            return typeof newSegmentFilters[0].Value === 'string' ? newSegmentFilters[0].Value : newSegmentFilters[0].Value.name;
        }
        return originalSegmentFilter.Value;
    }

    close(): void {
        this.dialogRef.close();
    }
}
